import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/Layout";
import { CloudUpload, RefreshCw, Plug, Unplug, Loader2, Check, AlertTriangle, ExternalLink, CalendarDays, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/dateFormat";

/**
 * Admin-only panel — Google Sheets sync for the Registry.
 *
 *   Not connected → "Connect Google" button → opens /api/integrations/google/connect
 *   Connected      → status row + "Sync now" + "Disconnect" + nightly toggle
 *
 * After Google redirects back the URL will contain ?google=ok|err. We surface a
 * toast and refresh the panel state.
 */
export default function GoogleSheetsPanel() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, _error: formatApiError(e) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle ?google=ok|err redirect from OAuth callback
  useEffect(() => {
    const flag = params.get("google");
    if (!flag) return;
    if (flag === "ok") toast.success("Google connected");
    else toast.error(`Google connect failed (${params.get("reason") || "unknown"})`);
    setParams((p) => { p.delete("google"); p.delete("reason"); return p; }, { replace: true });
    load();
  }, [params, setParams, load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/integrations/google/connect");
      if (data?.auth_url) window.location.href = data.auth_url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? The Sheet stays in your Google Drive but we'll forget the link.")) return;
    setBusy("disconnect");
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Google disconnected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/integrations/google/sync-now");
      const c = data?.counts || {};
      toast.success(`Synced · ${Object.entries(c).map(([k, v]) => `${k}:${v}`).join(" · ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const setupStudioCalendar = async () => {
    setBusy("cal-setup");
    try {
      const { data } = await api.post("/integrations/google/calendar/setup-studio");
      toast.success(`Studio calendar ready (${(data?.calendar_id || "").slice(0, 8)}…)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const syncCalendarNow = async () => {
    setBusy("cal-sync");
    try {
      const { data } = await api.post("/integrations/google/calendar/sync-now");
      const personal = data?.personal_leaves || 0;
      const studio = data?.studio || {};
      const parts = [`leaves:${personal}`];
      if (Object.keys(studio).length) parts.push(...Object.entries(studio).map(([k, v]) => `${k}:${v}`));
      toast.success(`Calendar synced · ${parts.join(" · ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleNightly = async (enabled) => {
    setBusy("nightly");
    try {
      await api.post("/integrations/google/nightly", { enabled });
      toast.success(`Nightly sync ${enabled ? "enabled (02:00 IST)" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!status) {
    return (
      <div className="card-flat p-4 text-xs text-slate-400 inline-flex items-center gap-2">
        <Loader2 size={11} className="animate-spin" /> Checking Google connection…
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="card-flat p-4 border-l-4 border-l-[#F54800]" data-testid="google-sheets-panel">
        <div className="flex items-start gap-3">
          <CloudUpload size={18} className="text-[#F54800] mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-display font-bold text-slate-900">Google — not connected</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Connect your Google account to {status.is_admin ? "mirror the Registry into a master Sheet AND push milestones/reviews/leaves to Google Calendar" : "sync your approved leaves to your Google Calendar and see your upcoming events on the dashboard"}.
            </div>
          </div>
          <Button
            onClick={connect}
            disabled={busy === "connect"}
            className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
            data-testid="google-connect-btn"
          >
            {busy === "connect" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Plug size={12} className="mr-1.5" />}
            Connect Google
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="google-sheets-panel">
      {/* ---------- Connection chip + Sheets row (admin only) ---------- */}
      {status.is_admin && (
        <div className="card-flat p-4">
          <div className="flex flex-wrap items-start gap-3">
            <CloudUpload size={18} className="text-green-600 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-display font-bold text-slate-900 inline-flex items-center gap-2">
                Google Sheets <Tag tone="green">connected</Tag>
                {status.google_email && <span className="text-[11px] text-slate-500 font-normal">· {status.google_email}</span>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-3">
                {status.spreadsheet_url ? (
                  <a href={status.spreadsheet_url} target="_blank" rel="noreferrer" className="text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid="sheet-link">
                    <ExternalLink size={10} /> Open Sheet
                  </a>
                ) : (
                  <span className="text-slate-400">Sheet will be created on first sync</span>
                )}
                {status.last_synced_at && (
                  <span data-testid="last-synced">
                    Last synced · {formatDateTime(status.last_synced_at)}
                  </span>
                )}
                {status.last_sync_counts && (
                  <span className="font-mono text-[10px] text-slate-400">
                    {Object.entries(status.last_sync_counts).map(([k, v]) => `${k}:${v}`).join(" · ")}
                  </span>
                )}
                {status.last_sync_error && (
                  <span className="text-red-600 inline-flex items-center gap-1"><AlertTriangle size={10} /> {status.last_sync_error}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <Button onClick={syncNow} disabled={busy === "sync"} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs" data-testid="sync-now-btn">
                {busy === "sync" ? <Loader2 size={11} className="animate-spin mr-1.5" /> : <RefreshCw size={11} className="mr-1.5" />}
                Sync now
              </Button>
              <Button onClick={() => toggleNightly(!status.nightly_enabled)} disabled={busy === "nightly"} variant="outline" className="rounded-sm h-8 text-xs" data-testid="nightly-toggle-btn" title="When on, the sync runs every night at 02:00 IST.">
                {status.nightly_enabled ? <Check size={11} className="mr-1.5 text-green-600" /> : null}
                Nightly · {status.nightly_enabled ? "ON" : "off"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Calendar row (all internal users) ---------- */}
      <div className="card-flat p-4" data-testid="google-calendar-row">
        <div className="flex flex-wrap items-start gap-3">
          <CalendarDays size={18} className="text-blue-600 mt-0.5" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-display font-bold text-slate-900 inline-flex items-center gap-2">
              Google Calendar <Tag tone={status.studio_calendar_id || !status.is_admin ? "green" : "yellow"}>
                {status.is_admin ? (status.studio_calendar_id ? "connected" : "needs setup") : "personal"}
              </Tag>
              {!status.is_admin && status.google_email && (
                <span className="text-[11px] text-slate-500 font-normal">· {status.google_email}</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-3">
              {status.is_admin && status.studio_calendar_id && (
                <span className="font-mono text-[10px]">Studio calendar · {status.studio_calendar_id.slice(0, 18)}…</span>
              )}
              {status.is_admin && !status.studio_calendar_id && (
                <span className="text-yellow-700">Click &quot;Setup studio calendar&quot; to create the shared &quot;Analogy Studio&quot; calendar.</span>
              )}
              {status.last_calendar_synced_at && (
                <span data-testid="last-cal-synced">
                  Last synced · {formatDateTime(status.last_calendar_synced_at)}
                </span>
              )}
              {status.last_calendar_sync_counts && (
                <span className="font-mono text-[10px] text-slate-400">
                  leaves:{status.last_calendar_sync_counts.personal_leaves || 0}
                  {status.last_calendar_sync_counts.studio && Object.entries(status.last_calendar_sync_counts.studio).map(([k, v]) => ` · ${k}:${v}`).join("")}
                </span>
              )}
              {status.last_calendar_sync_error && (
                <span className="text-red-600 inline-flex items-center gap-1"><AlertTriangle size={10} /> {status.last_calendar_sync_error}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {status.is_admin && !status.studio_calendar_id && (
              <Button onClick={setupStudioCalendar} disabled={busy === "cal-setup"} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white h-8 text-xs" data-testid="cal-setup-btn">
                {busy === "cal-setup" ? <Loader2 size={11} className="animate-spin mr-1.5" /> : <CalendarPlus size={11} className="mr-1.5" />}
                Setup studio calendar
              </Button>
            )}
            <Button onClick={syncCalendarNow} disabled={busy === "cal-sync"} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs" data-testid="cal-sync-now-btn">
              {busy === "cal-sync" ? <Loader2 size={11} className="animate-spin mr-1.5" /> : <RefreshCw size={11} className="mr-1.5" />}
              Sync calendar
            </Button>
          </div>
        </div>
      </div>

      {/* ---------- Disconnect ---------- */}
      <div className="flex items-center justify-end">
        <button
          onClick={disconnect}
          disabled={busy === "disconnect"}
          className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-1"
          data-testid="google-disconnect-btn"
        >
          <Unplug size={10} /> Disconnect Google ({status.google_email})
        </button>
      </div>
    </div>
  );
}
